import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Box, Flex, Text, useColorMode } from '@chakra-ui/react';
import { takeLinkedId } from '../../hooks/useUrlSyncedId';
import { useTranslation } from 'react-i18next';
import { searchMovements } from '../../api/endpointsBackend';
import { ConvertedAmountsProvider } from '../../contexts/ConvertedAmountsContext';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import { useCategoriesView } from '../../hooks/useCategoriesView';
import i18n from '../../i18n';
import { monthLong } from '../../utils/intlFormatters';
import { categoryTint } from '../../utils/categoryTint';
import UnconvertibleNote from '../shared/UnconvertibleNote';
import PhoneSheet from './PhoneSheet';
import { usePhoneShell } from './PhoneShellContext';
import {
  AverageBar, DesktopNoteCard, Disc, ListCard, MovementRows, PhoneState, SectionCaption,
  Segments, ToggleRow, money, usePhoneHomeCurrency,
} from './PhoneReadOnlyParts';

const FALLBACK_COLOR = '#718096';

/** The month as it reads mid-sentence: "Spent in September", "Gastado en septiembre". */
const sentenceMonth = (idx) => {
  const name = monthLong(idx);
  return i18n.language?.startsWith('es') ? name.toLocaleLowerCase('es') : name;
};

/** How this month compares with the average, for expense and income alike. */
function useComparison(value, average, isExpense) {
  const { t } = useTranslation('categories');
  if (!average) return { label: null, fill: 'phone.muted', color: 'phone.muted' };
  const pct = Math.round(Math.abs(value / average - 1) * 100);
  const isOver = value > average;
  // More spending than usual is the one case worth a warning colour.
  const warn = isExpense && isOver && pct > 0;
  const good = isExpense ? !isOver : isOver;
  return {
    label: pct === 0 ? t('phone.onAverage') : t(isOver ? 'phone.over' : 'phone.under', { pct }),
    color: warn ? 'redesign.text.warn' : 'phone.muted',
    fill: warn ? 'redesign.accent.amber' : good ? 'phone.income' : 'phone.muted',
  };
}

function CategoryRow({
  row, isExpense, homeCurrency, onClick,
}) {
  const { t } = useTranslation('categories');
  const { colorMode } = useColorMode();
  const cmp = useComparison(row.spent, row.sixMonthAvg, isExpense);
  const hasSpending = row.spent > 0;

  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="grid"
      gridTemplateColumns="40px minmax(0, 1fr) auto"
      columnGap="12px"
      rowGap="6px"
      alignItems="center"
      w="100%"
      minH="64px"
      py="10px"
      px="14px"
      textAlign="left"
      _active={{ bg: 'redesign.bg.hover' }}
    >
      <Box gridRow="1 / 3">
        <Disc tint={categoryTint(row.color || FALLBACK_COLOR, colorMode).bg}>{row.icon || row.name?.charAt(0)}</Disc>
      </Box>
      <Text textStyle="phoneRowName" color="redesign.heading" isTruncated>{row.name}</Text>
      <Text textStyle="phoneRowAmount" color="redesign.heading" textAlign="right" whiteSpace="nowrap">
        {/* One amount rule everywhere: only a foreign amount names its currency. */}
        {hasSpending ? money(row.currency && row.currency !== homeCurrency ? row.currency : null, row.spent) : '—'}
      </Text>
      {hasSpending && row.sixMonthAvg > 0 ? (
        <>
          <AverageBar value={row.spent} average={row.sixMonthAvg} fill={cmp.fill} />
          <Text textStyle="phoneMeta" color={cmp.color} textAlign="right" whiteSpace="nowrap">{cmp.label}</Text>
        </>
      ) : (
        <Text gridColumn="2 / 4" textStyle="phoneMeta" color="phone.muted">
          {hasSpending
            ? t(row.count === 1 ? 'phone.countOne' : 'phone.countOther', { count: row.count })
            : t('phone.noneThisMonth')}
        </Text>
      )}
    </Box>
  );
}

/**
 * One category, read-only: this month's total against its 6-month average,
 * and the month's movements, each opening the movement sheet.
 */
function CategorySheet({
  row, isOpen, onClose, monthIdx, dateRange, isExpense, converted, homeCurrency, figureCurrency,
}) {
  const { t } = useTranslation('categories');
  const { colorMode } = useColorMode();
  const { openMovement, refreshSignal } = usePhoneShell();
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const cmp = useComparison(row?.spent || 0, row?.sixMonthAvg || 0, isExpense);

  const categoryId = row?.id;
  const currency = converted ? null : row?.currency;
  useEffect(() => {
    if (!isOpen || categoryId == null || !dateRange) return undefined;
    let active = true;
    // Ignored movements are left out of the month's total, so out of its list too.
    const filters = [
      { entity: 'Movement', column: 'category_id', operator: 'in', values: [categoryId] },
      { entity: 'Movement', column: 'is_ignored', operator: 'eq', values: [false] },
    ];
    if (currency) filters.push({ entity: 'Movement', column: 'currency', operator: 'in', values: [currency] });
    setLoading(true);
    searchMovements(filters, [{ entity: 'Movement', column: 'date', sort: 'desc' }], dateRange.start_date, dateRange.end_date, 31, 0)
      .then((res) => { if (active) setMovements(res?.items || []); })
      .catch(() => { if (active) setMovements([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isOpen, categoryId, currency, dateRange, refreshSignal]);

  useEffect(() => {
    if (!isOpen) setMovements([]);
  }, [isOpen]);

  const month = sentenceMonth(monthIdx);
  const color = row?.color || FALLBACK_COLOR;

  return (
    <PhoneSheet id="category" isOpen={isOpen} onClose={onClose} size="full" title={row?.name}>
      {row && (
        <>
          <Flex direction="column" align="center" pt="14px" px="16px" textAlign="center">
            <Disc size={72} fontSize="34px" tint={categoryTint(color, colorMode).bg}>{row.icon || row.name?.charAt(0)}</Disc>
            <Text fontSize="14px" lineHeight="20px" color="phone.muted" mt="12px">
              {t(isExpense ? 'phone.spentIn' : 'phone.earnedIn', { month })}
            </Text>
            <Text fontSize="28px" lineHeight="34px" fontWeight="700" letterSpacing="-0.02em" color="redesign.heading" mt="2px" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {/* A category with no movements has no currency of its own: its zero takes the page's. */}
              {converted && row.isConverted && row.spent > 0 ? '≈ ' : ''}{money(row.currency || figureCurrency, row.spent)}
            </Text>
          </Flex>

          {row.sixMonthAvg > 0 ? (
            <Box mx="16px" mt="24px">
              <Flex justify="space-between" align="baseline" gap="8px" textStyle="phoneMeta">
                <Box as="span" color="phone.muted" minW={0}>
                  {t('summary.sixMonthAvg')}{' '}
                  <Box as="span" color="redesign.text">{money(row.currency, row.sixMonthAvg)}</Box>
                </Box>
                {cmp.label && (
                  <Box as="span" color={cmp.color} fontWeight="600" flexShrink={0}>{cmp.label}</Box>
                )}
              </Flex>
              <Box mt="10px">
                <AverageBar value={row.spent} average={row.sixMonthAvg} fill={cmp.fill} />
              </Box>
            </Box>
          ) : (
            <Text textStyle="phoneMeta" color="phone.muted" textAlign="center" mx="16px" mt="12px">
              {t('phone.noAverage')}
            </Text>
          )}

          <DesktopNoteCard />

          {/* An empty month says so once, in the empty line below. */}
          {(loading || movements.length > 0) && (
            <SectionCaption>
              {t(movements.length === 1 ? 'phone.captionOne' : 'phone.captionOther', { month, count: loading ? row.count : movements.length })}
            </SectionCaption>
          )}
          <MovementRows
            movements={movements}
            loading={loading}
            emptyLabel={t('phone.noneThisMonth')}
            onOpen={openMovement}
            homeCurrency={homeCurrency}
          />
        </>
      )}
    </PhoneSheet>
  );
}

/**
 * Categories on the phone: this month's spending (or income) per category
 * against each one's 6-month average, read-only. A row opens its detail
 * sheet; editing categories stays on the computer.
 */
export default function PhoneCategories() {
  return (
    <ConvertedAmountsProvider>
      <PhoneCategoriesList />
    </ConvertedAmountsProvider>
  );
}

function PhoneCategoriesList() {
  const { t } = useTranslation('categories');
  const { displayCurrency } = useDisplayCurrency();
  const { refreshSignal } = usePhoneShell();
  const homeCurrency = usePhoneHomeCurrency();
  const [typeFilter, setTypeFilter] = useState('expenses');
  const [showOther, setShowOther] = useState(false);
  const [openKey, setOpenKey] = useState(null);

  const {
    loading, error, refetch,
    rowsWithSpending, rowsWithoutSpending,
    totalsByCurrency, sixMoAvgByCurrency, totalsConverted,
    monthDateRange, numUnconvertible, unconvertibleBuckets,
  } = useCategoriesView({ typeFilter, selectedCurrency: 'all', searchTerm: '', displayCurrency });

  // A movement fixed from a detail sheet can move money between categories.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    if (refreshSignal) refetchRef.current();
  }, [refreshSignal]);

  const isExpense = typeFilter === 'expenses';
  const monthIdx = monthDateRange ? Number(monthDateRange.start_date.slice(5, 7)) - 1 : new Date().getMonth();
  const month = sentenceMonth(monthIdx);
  const converted = Boolean(displayCurrency);
  const rowKey = (r) => `${r.id}::${r.currency || 'none'}`;

  // Kept while the sheet slides out, so its content does not blank first.
  const allRows = useMemo(() => [...rowsWithSpending, ...rowsWithoutSpending], [rowsWithSpending, rowsWithoutSpending]);
  const [lastRow, setLastRow] = useState(null);
  const openRow = allRows.find((r) => rowKey(r) === openKey) || null;
  useEffect(() => { if (openRow) setLastRow(openRow); }, [openRow]);

  // `?category_id=<id>` (a category in the chat, or a link from the desktop)
  // opens that category's sheet once the list is in: the page opens on
  // expenses, so an income category is looked for there next.
  const [linkedCategory, setLinkedCategory] = useState(null);
  useEffect(() => {
    const linked = takeLinkedId('category_id');
    if (linked) setLinkedCategory(linked);
  }, []);
  useEffect(() => {
    if (linkedCategory == null || loading) return;
    const row = allRows.find((r) => r.id === linkedCategory);
    if (row) {
      setOpenKey(rowKey(row));
      setLinkedCategory(null);
    } else if (typeFilter === 'expenses') {
      setTypeFilter('income');
    } else {
      setLinkedCategory(null);
    }
  }, [linkedCategory, loading, allRows, typeFilter]);

  const totals = Object.entries(totalsByCurrency).sort((a, b) => b[1] - a[1]);
  // An empty month still shows one zero, in the page's currency, with its average under it.
  const headline = totals.length ? totals : [[displayCurrency || homeCurrency, 0]];
  // Converted, every row is in the display currency the header names.
  const rowHome = converted ? displayCurrency : homeCurrency;

  let list;
  if (loading && allRows.length === 0) list = <PhoneState loading />;
  else if (error) list = <PhoneState>{t('phone.loadError')}</PhoneState>;
  else if (allRows.length === 0) list = <PhoneState>{t('empty.noCategories')}</PhoneState>;
  else {
    list = (
      <>
        {rowsWithSpending.length > 0 ? (
          <ListCard>
            {rowsWithSpending.map((r) => (
              <CategoryRow key={rowKey(r)} row={r} isExpense={isExpense} homeCurrency={rowHome} onClick={() => setOpenKey(rowKey(r))} />
            ))}
          </ListCard>
        ) : (
          <PhoneState>{t('phone.noneThisMonth')}</PhoneState>
        )}
        {rowsWithoutSpending.length > 0 && (
          <ToggleRow onClick={() => setShowOther((v) => !v)}>
            {t(showOther ? 'phone.hideOther' : 'phone.showOther', { count: rowsWithoutSpending.length })}
          </ToggleRow>
        )}
        {showOther && rowsWithoutSpending.length > 0 && (
          <ListCard mt="4px">
            {rowsWithoutSpending.map((r) => (
              <CategoryRow key={rowKey(r)} row={r} isExpense={isExpense} homeCurrency={rowHome} onClick={() => setOpenKey(rowKey(r))} />
            ))}
          </ListCard>
        )}
      </>
    );
  }

  return (
    <Box pb="8px">
      <Box px="16px" pt="12px">
        <Text textStyle="phoneMeta" color="phone.muted">
          {t(isExpense ? 'phone.spentIn' : 'phone.earnedIn', { month })}
        </Text>
        {headline.map(([currency, total]) => (
          <Text key={currency || 'none'} fontSize="28px" lineHeight="34px" fontWeight="700" letterSpacing="-0.02em" color="redesign.heading" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {converted && totalsConverted && total > 0 ? '≈ ' : ''}{money(currency, total)}
          </Text>
        ))}
        {headline.length === 1 && sixMoAvgByCurrency[headline[0][0]] > 0 && (
          <Text textStyle="phoneMeta" color="phone.muted" mt="2px">
            {t('summary.sixMonthAvg')} {money(headline[0][0], sixMoAvgByCurrency[headline[0][0]])}
          </Text>
        )}
        {converted && numUnconvertible > 0 && (
          // The note's info button grows to a thumb-sized target, as on Home.
          <Box mt="6px" sx={{ '& button': { minW: '44px', minH: '44px', justifyContent: 'center', ml: '-10px' } }}>
            <UnconvertibleNote buckets={unconvertibleBuckets} count={numUnconvertible} fontSize="13px" />
          </Box>
        )}
      </Box>

      <Box px="16px" mt="16px">
        <Segments
          label={t('phone.typeAria')}
          value={typeFilter}
          onChange={(v) => { setTypeFilter(v); setShowOther(false); }}
          options={[
            { value: 'expenses', label: t('filters.expenses') },
            { value: 'income', label: t('filters.income') },
          ]}
        />
      </Box>

      {list}

      <DesktopNoteCard />

      <CategorySheet
        row={openRow || lastRow}
        isOpen={Boolean(openRow)}
        onClose={() => setOpenKey(null)}
        monthIdx={monthIdx}
        dateRange={monthDateRange}
        isExpense={isExpense}
        converted={converted}
        homeCurrency={homeCurrency}
        figureCurrency={rowHome}
      />
    </Box>
  );
}
